<template>
  <section class="ticket-section">
    <div class="kv-header">
      <h2 class="ticket-section-title">Kostenvoranschläge</h2>
      <button
        v-if="canCreate"
        type="button"
        class="btn btn-primary btn-sm"
        @click="onCreate"
      >KV erstellen</button>
    </div>

    <p v-if="loadError" class="error">{{ loadError }}</p>

    <table v-if="estimates.length" class="kv-table">
      <thead>
        <tr>
          <th>KV-ID</th>
          <th>Datum</th>
          <th class="kv-num">Netto</th>
          <th class="kv-num">MwSt</th>
          <th class="kv-num">Brutto</th>
          <th>Ersteller</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="kv in estimates" :key="kv.id">
          <td data-label="KV-ID">{{ kv.id }}</td>
          <td data-label="Datum">{{ formatDate(kv.creation_timestamp) }}</td>
          <td class="kv-num" data-label="Netto">{{ money(kv.net_price) }}</td>
          <td class="kv-num" data-label="MwSt">{{ money(kv.vat) }}</td>
          <td class="kv-num" data-label="Brutto">{{ money(kv.bru_price) }}</td>
          <td data-label="Ersteller">{{ kv.creator_name }}</td>
          <td class="kv-actions">
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              :disabled="pdfBusyId === kv.id"
              @click="onOpenPdf(kv.id)"
            >{{ pdfBusyId === kv.id ? 'Öffne…' : 'PDF' }}</button>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary kv-delete-btn"
              :disabled="deleteBusyId === kv.id"
              @click="onDelete(kv.id)"
            >{{ deleteBusyId === kv.id ? 'Lösche…' : 'Löschen' }}</button>
          </td>
        </tr>
      </tbody>
    </table>
    <p v-else-if="!loadError" class="kv-empty">Noch kein Kostenvoranschlag.</p>

    <CostEstimateCreateModal ref="createModal" />
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';
import CostEstimateCreateModal from './CostEstimateCreateModal.vue';
import {
  listCostEstimates,
  fetchCostEstimatePdfBlob,
  deleteCostEstimate,
  type CostEstimate,
} from '../services/costEstimates';
import { hasProcess, PROCESS_KOSTENVORANSCHLAG, type TicketDetail } from '../services/tickets';

const props = defineProps<{ ticket: TicketDetail }>();
const emit  = defineEmits<{ changed: [] }>();

const estimates    = ref<CostEstimate[]>([]);
const loadError    = ref<string | null>(null);
const pdfBusyId    = ref<number | null>(null);
const deleteBusyId = ref<number | null>(null);

const createModal = useTemplateRef<InstanceType<typeof CostEstimateCreateModal>>('createModal');

// Verfügbarkeit kommt vom Server (available_processes) — kein Statusstring im Frontend.
const canCreate = computed(() => hasProcess(props.ticket, PROCESS_KOSTENVORANSCHLAG));

async function reload(): Promise<void> {
  try {
    estimates.value = await listCostEstimates(props.ticket.id);
    loadError.value = null;
  } catch (e) {
    loadError.value = e instanceof Error ? e.message : 'Kostenvoranschläge konnten nicht geladen werden.';
  }
}

onMounted(reload);
watch(() => props.ticket.id, reload);

async function onCreate(): Promise<void> {
  const created = await createModal.value?.open(props.ticket);
  if (created) {
    await reload();
    emit('changed'); // Ticket-Verlauf aktualisieren
  }
}

// openCreate: erlaubt der Prozessleiste, das Anlegen-Formular zu oeffnen.
// reload: nötig, wenn ein KV ausserhalb dieses Panels entsteht (kombinierter
// Prozess „KV/Rep-Bericht erstellen“) — die Ticket-ID aendert sich dabei nicht,
// der watch oben feuert also nicht.
defineExpose({ openCreate: onCreate, reload });

async function onDelete(kvId: number): Promise<void> {
  if (deleteBusyId.value !== null) return;
  if (!window.confirm(`Kostenvoranschlag KV-${kvId} wirklich löschen?`)) return;
  deleteBusyId.value = kvId;
  try {
    await deleteCostEstimate(props.ticket.id, kvId);
    await reload();
    emit('changed'); // Ticket-Verlauf aktualisieren
  } catch (e) {
    loadError.value = e instanceof Error ? e.message : 'KV konnte nicht gelöscht werden.';
  } finally {
    deleteBusyId.value = null;
  }
}

async function onOpenPdf(kvId: number): Promise<void> {
  if (pdfBusyId.value !== null) return;
  pdfBusyId.value = kvId;
  try {
    const blob = await fetchCostEstimatePdfBlob(props.ticket.id, kvId);
    const url  = URL.createObjectURL(blob);
    window.open(url, '_blank');
    // URL nach kurzer Zeit freigeben (Tab hat sie dann geladen).
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    loadError.value = e instanceof Error ? e.message : 'PDF konnte nicht geöffnet werden.';
  } finally {
    pdfBusyId.value = null;
  }
}

function money(value: number | null): string {
  if (value === null) return '—';
  return value.toLocaleString('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(value: string): string {
  if (!value) return '—';
  const d = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('de-AT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(d);
}
</script>

<style scoped>
.kv-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
/* .ticket-section-title jetzt global in apple.css */
.kv-actions {
  display: flex;
  gap: 6px;
  white-space: nowrap;
}
/* Löschen bleibt dezent (neutraler Ghost), erst beim Hover rot. */
.kv-delete-btn:hover:not(:disabled) {
  color: var(--danger);
  border-color: rgba(215, 0, 21, 0.35);
  background: rgba(215, 0, 21, 0.06);
}
.kv-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.kv-table th,
.kv-table td {
  padding: 6px 8px;
  border-bottom: 1px solid var(--border, #e5e7eb);
  text-align: left;
}
.kv-num { text-align: right; font-variant-numeric: tabular-nums; }
.kv-empty { color: var(--muted, #6c757d); margin: 0; }

/* Handyansicht: Tabelle → gestapelte Karten (Label : Wert), kein Überlauf. */
@media (max-width: 640px) {
  .kv-header {
    flex-wrap: wrap;
    gap: 8px;
  }

  .kv-table thead {
    display: none;
  }

  .kv-table,
  .kv-table tbody,
  .kv-table tr,
  .kv-table td {
    display: block;
    width: 100%;
  }

  .kv-table tr {
    border: 1px solid var(--border, #e5e7eb);
    border-radius: var(--radius-sm, 8px);
    padding: 8px 12px;
    margin-bottom: 10px;
  }

  .kv-table td {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    padding: 3px 0;
    border: none;
    text-align: right;
  }

  /* Label aus data-label vor jeden Wert. */
  .kv-table td::before {
    content: attr(data-label);
    color: var(--muted, #6c757d);
    font-weight: 600;
    text-align: left;
  }

  .kv-num { text-align: right; }

  /* Aktionszeile: Buttons rechts, ohne Label. */
  .kv-actions {
    justify-content: flex-end;
    padding-top: 8px;
  }
  .kv-actions::before {
    display: none;
  }
  .kv-actions .btn {
    flex: 1 1 auto;
  }
}
</style>
